// One indexable page per Palworld merchandise line.
//
// Generalist "best Palworld merch" listicles rank with no photo, no price and no
// buy path. Each of these pages carries the real product photo, the live
// marketplace asking price, Product/AggregateOffer schema, answer-first buying
// checks and both affiliate buy buttons.
import { useMemo } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { breadcrumbSchema, webPageSchema, productSchema } from "@/lib/schema";
import { MERCH_LINES, MERCH_LINE_BY_SLUG, merchImage, merchProduct } from "@/lib/merch-lines";
import { getPriceBoard } from "@/lib/prices.functions";
import { priceBoardQueryOptions } from "@/lib/price-board-query";
import { buildSealedRows, formatUsd, PRICE_SOURCES } from "@/lib/prices-shared";
import { goUrl } from "@/lib/outbound";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";

const SITE = "https://palstars.com";

export const Route = createFileRoute("/merch/$line")({
  beforeLoad: ({ params }) => {
    if (!MERCH_LINE_BY_SLUG.has(params.line)) throw redirect({ to: "/merch", replace: true });
  },
  // Server-render the live sealed price row so it ships in the page HTML.
  loader: ({ context }) => context.queryClient.ensureQueryData(priceBoardQueryOptions()),
  head: ({ params }) => {
    const line = MERCH_LINE_BY_SLUG.get(params.line);
    if (!line) return {};
    const product = merchProduct(line);
    const img = merchImage(line);
    const url = `${SITE}/merch/${line.slug}`;
    const image = img ? (img.src.startsWith("http") ? img.src : `${SITE}${img.src}`) : undefined;
    return {
      meta: [
        { title: line.title },
        { name: "description", content: line.description },
        { property: "og:title", content: line.title },
        { property: "og:description", content: line.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: line.title },
        { name: "twitter:description", content: line.description },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
        { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbSchema([["Home", "/"], ["Palworld Merch", "/merch"], [line.h1, `/merch/${line.slug}`]]),
        webPageSchema({ name: line.h1, description: line.description, path: `/merch/${line.slug}` }),
        faqSchema(line.faq, url),
        ...(product
          ? [
              productSchema({
                name: product.name,
                description: line.description,
                path: `/merch/${line.slug}`,
                ...(image ? { image } : {}),
                sku: product.key,
                brand: line.licensee,
                category: "Palworld merchandise",
                lowPrice: product.msrp,
                offerUrl: url,
              }),
            ]
          : []),
      ],
    };
  },
  component: MerchLinePage,
});

function MerchLinePage() {
  const { line: slug } = Route.useParams();
  const line = MERCH_LINE_BY_SLUG.get(slug)!;
  const product = merchProduct(line);
  const img = merchImage(line);

  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    refetchOnMount: "always",
    staleTime: 5 * 60 * 1000,
  });
  const row = useMemo(
    () => buildSealedRows(data?.sealed ?? []).find((r) => r.key === line.key) ?? null,
    [data?.sealed, line.key],
  );

  const ebay = PRICE_SOURCES.find((s) => s.id === "ebay");
  const tcg = PRICE_SOURCES.find((s) => s.id === "tcgplayer");
  const query = product?.query ?? "Palworld official merchandise";

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/merch" className="hover:text-primary">
          Palworld merch
        </Link>{" "}
        · {line.h1}
      </nav>

      <header className="mt-2">
        <p className="text-xs uppercase tracking-widest text-primary">Palworld merchandise</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">{line.h1}</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">{line.answer}</p>
      </header>

      <section className="glass mt-6 grid gap-6 rounded-2xl p-5 sm:grid-cols-[minmax(0,220px)_1fr]">
        {img ? (
          <img
            src={img.src}
            alt={img.alt}
            width={440}
            height={440}
            loading="lazy"
            decoding="async"
            className="w-full rounded-xl object-cover"
          />
        ) : null}
        <div>
          <h2 className="text-lg font-bold">{product?.name ?? line.h1}</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Licensed by {line.licensee}
            {product ? ` · ${product.detail}` : ""}
          </p>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">Live asking price</dt>
              <dd className="text-2xl font-extrabold tabular-nums">{row?.market ? formatUsd(row.market) : "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Retail (MSRP)</dt>
              <dd className="text-2xl font-extrabold tabular-nums">{product ? formatUsd(product.msrp) : "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Listings sampled</dt>
              <dd className="text-2xl font-extrabold tabular-nums">{row?.listings ?? 0}</dd>
            </div>
          </dl>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px]">
            {ebay ? (
              <a
                href={goUrl(ebay.id, "merch", query)}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={() => trackAffiliateClick({ source: ebay.id, kind: "merch", targetKey: line.key })}
                className="btn-ebay"
              >
                Buy On Ebay
              </a>
            ) : null}
            {tcg ? (
              <a
                href={goUrl(tcg.id, "merch", query)}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={() => trackAffiliateClick({ source: tcg.id, kind: "merch", targetKey: line.key })}
                className="btn-tcgplayer"
              >
                Buy TCGPlayer
              </a>
            ) : null}
          </div>
          {img && !img.official ? (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Image is official announcement art; retail product photography for this line is not yet published.
            </p>
          ) : null}
        </div>
      </section>

      <AffiliateDisclosure className="mt-6" updated={data?.updated ?? null} />

      <section className="glass mt-8 rounded-2xl p-6">
        <h2 className="text-xl font-bold">Before you buy</h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          {line.checks.map((c) => (
            <li key={c} className="aeo-answer">
              · {c}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-bold">Other Palworld merch lines</h2>
        <div className="mt-3 flex flex-wrap gap-2 text-sm">
          {MERCH_LINES.filter((l) => l.slug !== line.slug).map((l) => (
            <Link
              key={l.slug}
              to="/merch/$line"
              params={{ line: l.slug }}
              className="glass rounded-full px-3 py-1.5 hover:text-primary"
            >
              {l.h1}
            </Link>
          ))}
        </div>
      </section>

      <InternalLinkBlock path="/merch" />
      <FaqSection items={line.faq as FaqItem[]} heading={`${line.h1} FAQ`} />
    </div>
  );
}
